import { api } from '@/lib/api';
import { useDiscoveryStore } from '@/stores/discovery';

/**
 * Orchestration du mode découverte côté client : combine le store discovery
 * (pont X-Sandbox + état de visite) avec les endpoints du bac à sable
 * (`/sandbox/seed`, `/sandbox/reset`). Centralisé ici pour que le lanceur, la
 * page de découverte et le bandeau partagent exactement la même logique.
 */

let seeding: Promise<void> | null = null;

/**
 * Garantit que l'on est en mode découverte avec un bac à sable peuplé.
 * Idempotent : ne ré-ensemence pas si le mode est déjà actif (on ne veut pas
 * écraser ce que l'utilisateur a fait dans son espace). Le seed n'a lieu qu'à
 * la première entrée.
 */
export async function ensureDiscovery(): Promise<void> {
  const discovery = useDiscoveryStore();
  if (discovery.active) return;
  if (seeding) return seeding;

  // Active le pont AVANT le seed pour que l'insertion soit taguée au bon owner
  // et retombe dans le bac à sable (l'en-tête X-Sandbox part avec la requête).
  discovery.start();
  seeding = api
    .post('/sandbox/seed')
    .then(() => undefined)
    .finally(() => {
      seeding = null;
    });
  return seeding;
}

/** Remet le bac à sable dans son état initial (ré-ensemence côté serveur). */
export async function resetSandbox(): Promise<void> {
  const discovery = useDiscoveryStore();
  if (!discovery.active) return;
  await api.post('/sandbox/reset');
  discovery.closeTour();
}

/** Quitte le mode découverte : retour aux vraies données. */
export function exitDiscovery(): void {
  const discovery = useDiscoveryStore();
  discovery.stop();
}
